import { Button } from '@shared/components/ui/button';
import type { ButtonProps } from '@shared/components/ui/button/Button';
import { Loader2, LogOut } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLogout } from '../hooks/useAuth';

interface LogoutButtonProps extends Omit<ButtonProps, 'onClick'> {
  showIcon?: boolean;
  confirmLogout?: boolean;
  onLogoutStart?: () => void;
  onLogoutComplete?: () => void;
}

export const LogoutButton: React.FC<LogoutButtonProps> = ({
  showIcon = true,
  confirmLogout = false,
  onLogoutStart,
  onLogoutComplete,
  children,
  variant = 'ghost',
  size = 'sm',
  className = '',
  ...buttonProps
}) => {
  const { t } = useTranslation('auth');
  const logoutMutation = useLogout();

  const handleLogout = async () => {
    if (confirmLogout) {
      const confirmed = window.confirm(t('logout.confirm', '¿Estás seguro de que quieres cerrar sesión?'));
      if (!confirmed) return;
    }

    try {
      onLogoutStart?.();
      await logoutMutation.mutateAsync();
      onLogoutComplete?.();
    } catch (error) {
      console.error('Error al hacer logout:', error);
      // Incluso si hay error, el logout se maneja en el hook
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleLogout}
      disabled={logoutMutation.isPending}
      className={`${className}`}
      data-testid="logout-button"
      {...buttonProps}
    >
      {logoutMutation.isPending ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {t('logout.loading', 'Cerrando...')}
        </>
      ) : (
        <>
          {showIcon && <LogOut className="mr-2 h-4 w-4" />}
          {children || t('logout.button', 'Cerrar Sesión')}
        </>
      )}
    </Button>
  );
};

export default LogoutButton;
